import React, { useState, useEffect } from "react";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import {
  Heart,
  ArrowRight,
  BookOpen,
  MessageCircle,
  Award,
  Users,
  Moon,
  Sun,
} from "lucide-react";
import { Helmet } from "react-helmet";
const SelfCareAfterBreakup = () => {
  const [activeTab, setActiveTab] = useState("strategies");

  // Load Google AdSense script
  useEffect(() => {
    const script = document.createElement("script");
    script.async = true;
    script.src =
      "https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-2067522743161930";
    script.crossOrigin = "anonymous";
    document.head.appendChild(script);
    document.title = "Self-Care After a Breakup | Relationship Wellness";
    // Push ads to adsbygoogle
    (window.adsbygoogle = window.adsbygoogle || []).push({});
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  // Self-care strategies data
  const strategies = [
    {
      id: 1,
      title: "Allow Yourself to Grieve",
      description:
        "Give yourself permission to feel all emotions without judgment. Sadness, anger, relief, confusion—all are valid parts of the healing process.",
      tips: [
        "Set aside time each day for emotional processing",
        "Try journaling about your feelings without censoring yourself",
        "Consider talking to a trusted friend who can listen without judgment",
        "Remember that grief isn't linear—some days will be harder than others",
      ],
      icon: <Heart className="text-rose-500" size={24} />,
    },
    {
      id: 2,
      title: "Reconnect with Yourself",
      description:
        "Relationships often involve compromise, and you may have set aside parts of yourself. Now is the time to rediscover who you are as an individual.",
      tips: [
        "Make a list of activities that brought you joy before the relationship",
        "Try something completely new that you've always been curious about",
        "Reflect on your personal values and goals independent of a partner",
        "Create a vision board for your future that excites you",
      ],
      icon: <Moon className="text-indigo-500" size={24} />,
    },
    {
      id: 3,
      title: "Prioritize Physical Health",
      description:
        "Your physical and emotional well-being are deeply connected. Taking care of your body can help stabilize your mood and increase resilience.",
      tips: [
        "Aim for 7-8 hours of quality sleep each night",
        "Move your body daily, even if it's just a short walk",
        "Eat regular, nutritious meals even when your appetite changes",
        "Stay hydrated and limit alcohol which can worsen emotional volatility",
      ],
      icon: <Sun className="text-amber-500" size={24} />,
    },
    {
      id: 4,
      title: "Set Healthy Boundaries",
      description:
        "Creating space from your ex and potentially triggering situations gives you room to heal without constant reminders of the relationship.",
      tips: [
        "Consider a period of no contact to gain perspective",
        "Mute or unfollow your ex on social media platforms",
        "Communicate your needs clearly to mutual friends",
        "Practice saying 'no' to situations that feel overwhelming",
      ],
      icon: <Users className="text-blue-500" size={24} />,
    },
    {
      id: 5,
      title: "Seek Support Systems",
      description:
        "You don't have to navigate this challenging time alone. Reaching out for support can provide perspective, comfort, and guidance.",
      tips: [
        "Reconnect with friends you may have seen less during your relationship",
        "Consider joining a support group of others experiencing similar situations",
        "Don't hesitate to seek professional support from a therapist or counselor",
        "Be specific about what kind of support you need from different people",
      ],
      icon: <MessageCircle className="text-green-500" size={24} />,
    },
    {
      id: 6,
      title: "Practice Mindfulness",
      description:
        "Staying present can help manage overwhelming emotions and reduce rumination about the past or anxiety about the future.",
      tips: [
        "Try guided meditation apps designed for emotional healing",
        "Practice deep breathing when emotions feel intense",
        "Engage fully in simple activities like cooking or walking",
        "Use grounding techniques when you feel overwhelmed",
      ],
      icon: <BookOpen className="text-purple-500" size={24} />,
    },
    {
      id: 7,
      title: "Celebrate Progress",
      description:
        "Healing isn't linear, and acknowledging your progress—no matter how small—can build momentum and hope during difficult times.",
      tips: [
        "Keep a 'wins' journal to document moments of strength or joy",
        "Notice when activities that were once painful become easier",
        "Reward yourself for taking steps toward healing",
        "Share your progress with trusted supporters who can celebrate with you",
      ],
      icon: <Award className="text-orange-500" size={24} />,
    },
  ];

  // Healing timeline data
  const timelineSteps = [
    {
      phase: "Initial Shock (Days 1-14)",
      description:
        "The initial period after a breakup often involves intense emotions and adjustment to the sudden change in your life circumstances.",
      feelings: [
        "Disbelief",
        "Intense sadness",
        "Anxiety",
        "Difficulty concentrating",
      ],
      selfCare: [
        "Focus on basic needs like eating and sleeping",
        "Reach out to close supports",
        "Give yourself permission to feel without judgment",
      ],
    },
    {
      phase: "Acute Grief (Weeks 2-6)",
      description:
        "As the reality sets in, you may experience waves of intense emotions as you begin processing the loss.",
      feelings: [
        "Waves of sadness",
        "Anger",
        "Bargaining",
        "Rumination about the relationship",
      ],
      selfCare: [
        "Establish routines",
        "Begin regular physical activity",
        "Set boundaries around contact with your ex",
        "Practice basic mindfulness",
      ],
    },
    {
      phase: "Adjustment (Months 2-4)",
      description:
        "During this period, you start adapting to your new reality and rebuilding your individual identity.",
      feelings: [
        "Lessening intensity of emotions",
        "Periods of acceptance mixed with setbacks",
        "Curiosity about the future",
      ],
      selfCare: [
        "Reconnect with old interests or try new ones",
        "Expand your social connections",
        "Reflect on lessons learned",
        "Create new traditions",
      ],
    },
    {
      phase: "Integration (Months 4-12)",
      description:
        "The relationship becomes part of your life story rather than defining your current experience.",
      feelings: [
        "Increased stability",
        "Perspective on the relationship",
        "Hope for the future",
        "Occasional nostalgia",
      ],
      selfCare: [
        "Set new personal goals",
        "Consider dating when it feels right",
        "Notice and celebrate your growth",
        "Share your wisdom with others",
      ],
    },
  ];

  // Resources data
  const resources = [
    {
      category: "Books",
      items: [
        {
          title: "Whole Again",
          author: "Jackson MacKenzie",
          focus: "Healing from trauma and rebuilding self-trust",
        },
        {
          title: "Getting Past Your Breakup",
          author: "Susan J. Elliott",
          focus: "Practical step-by-step approach to recovery",
        },
        {
          title: "Tiny Beautiful Things",
          author: "Cheryl Strayed",
          focus: "Essays on love, loss, and finding meaning",
        },
      ],
    },
    {
      category: "Podcasts",
      items: [
        {
          title: "Breakup Boost",
          host: "Trina Leckie",
          focus: "Practical advice for navigating breakup challenges",
        },
        {
          title: "Just Break Up",
          host: "Sierra DeMulder & Sam Blackwell",
          focus: "Honest conversations about relationships ending",
        },
        {
          title: "The Baggage Reclaim Sessions",
          host: "Natalie Lue",
          focus: "Rebuilding self-esteem and setting boundaries",
        },
      ],
    },
    {
      category: "Apps",
      items: [
        {
          title: "Mend",
          features:
            "Daily audio lessons, journaling prompts, and progress tracking",
        },
        {
          title: "Headspace",
          features:
            "Meditation programs specifically for heartbreak and difficult emotions",
        },
        {
          title: "Daylio",
          features:
            "Mood tracking to identify patterns and improvements over time",
        },
      ],
    },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-slate-50 to-slate-100 text-slate-800">
      <Header />
      <Helmet>
        <title>Self care after breakup</title>
        <meta
          name="description"
          content="What should you do after having a breakup? This article talks about self care tips one have to take after a breakup."
        />
      </Helmet>
      <div
        className="container mx-auto px-10 py-6"
        style={{ width: "100%", minWidth: "300px" }}>
        <ins
          className="adsbygoogle"
          style={{ display: "block" }}
          data-ad-client="ca-pub-2067522743161930"
          data-ad-slot="YYYYYYYYYY"
          data-ad-format="auto"
          data-full-width-responsive="true"></ins>
      </div>
      {/* Add top padding to push content below header */}
      <main className="flex-grow container mx-auto px-4 py-12 max-w-5xl pt-20">
        {/* Hero Section */}
        <div className="mb-12 text-center">
          <h1 className="text-5xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-rose-500">
            Self-Care After a Breakup
          </h1>
          <p className="text-xl max-w-3xl mx-auto text-slate-600">
            Navigating the end of a relationship is one of life's most
            challenging experiences. This guide offers compassionate strategies
            to support your healing journey and help you rediscover your
            strength and joy.
          </p>
          {/* Ad Unit 1: Below Hero Text */}
          <div className="mt-8">
            <ins
              className="adsbygoogle"
              style={{ display: "block" }}
              data-ad-client="ca-pub-2067522743161930"
              data-ad-slot="YOUR_AD_SLOT_ID_1" // Replace with actual ad slot ID
              data-ad-format="auto"
              data-full-width-responsive="true"></ins>
            <script>(adsbygoogle = window.adsbygoogle || []).push({});</script>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex flex-wrap mb-8 border-b border-slate-200">
          <button
            onClick={() => setActiveTab("strategies")}
            className={`px-6 py-3 font-medium text-lg ${
              activeTab === "strategies"
                ? "text-purple-600 border-b-2 border-purple-600"
                : "text-slate-500 hover:text-slate-800"
            }`}>
            Self-Care Strategies
          </button>
          <button
            onClick={() => setActiveTab("timeline")}
            className={`px-6 py-3 font-medium text-lg ${
              activeTab === "timeline"
                ? "text-purple-600 border-b-2 border-purple-600"
                : "text-slate-500 hover:text-slate-800"
            }`}>
            Healing Timeline
          </button>
          <button
            onClick={() => setActiveTab("resources")}
            className={`px-6 py-3 font-medium text-lg ${
              activeTab === "resources"
                ? "text-purple-600 border-b-2 border-purple-600"
                : "text-slate-500 hover:text-slate-800"
            }`}>
            Helpful Resources
          </button>
        </div>

        {/* Strategy Content */}
        {activeTab === "strategies" && (
          <div>
            <p className="text-lg mb-8 text-slate-600">
              Everyone's healing journey is unique, but these evidence-based
              strategies can help you navigate this challenging time with
              self-compassion and intentionality.
            </p>

            <div className="space-y-8">
              {strategies.map((strategy) => (
                <div
                  key={strategy.id}
                  className="bg-white rounded-lg shadow-md p-6 transition-all hover:shadow-lg">
                  <div className="flex items-start gap-4">
                    <div className="mt-1 p-2 bg-slate-100 rounded-full">
                      {strategy.icon}
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-slate-800 mb-2">
                        {strategy.id}. {strategy.title}
                      </h3>
                      <p className="text-slate-600 mb-4">
                        {strategy.description}
                      </p>

                      <div className="bg-slate-50 rounded-md p-4">
                        <h4 className="font-medium text-purple-600 mb-2">
                          Try these approaches:
                        </h4>
                        <ul className="space-y-2">
                          {strategy.tips.map((tip, index) => (
                            <li key={index} className="flex items-start gap-2">
                              <ArrowRight
                                size={18}
                                className="text-rose-500 mt-1 flex-shrink-0"
                              />
                              <span className="text-slate-700">{tip}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Ad Unit 2: Before Remember Note */}
            <div className="my-8">
              <ins
                className="adsbygoogle"
                style={{ display: "block" }}
                data-ad-client="ca-pub-2067522743161930"
                data-ad-slot="YOUR_AD_SLOT_ID_2" // Replace with actual ad slot ID
                data-ad-format="auto"
                data-full-width-responsive="true"></ins>
              <script>
                (adsbygoogle = window.adsbygoogle || []).push({});
              </script>
            </div>

            <div className="mt-12 bg-gradient-to-r from-purple-100 to-rose-100 p-6 rounded-lg">
              <h3 className="text-xl font-semibold mb-3">Remember</h3>
              <p className="text-slate-700">
                Healing is not linear, and there's no "right" timeline for
                moving forward. Be patient with yourself and recognize that some
                days will be harder than others. The goal isn't to forget the
                relationship but to integrate it into your life story in a way
                that allows you to move forward with wisdom and openness.
              </p>
            </div>
          </div>
        )}

        {/* Timeline Content */}
        {activeTab === "timeline" && (
          <div>
            <p className="text-lg mb-8 text-slate-600">
              While everyone's journey is unique, understanding the common
              phases of breakup recovery can help normalize your experience and
              provide perspective on your healing process.
            </p>

            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-9 top-0 bottom-0 w-1 bg-purple-200"></div>

              {/* Timeline events */}
              <div className="space-y-12">
                {timelineSteps.map((step, index) => (
                  <div key={index} className="relative">
                    <div className="absolute left-8 -translate-x-1/2 w-4 h-4 rounded-full bg-purple-500 border-4 border-white"></div>
                    <div className="ml-16">
                      <h3 className="text-xl font-semibold text-purple-600 mb-2">
                        {step.phase}
                      </h3>
                      <p className="text-slate-600 mb-4">{step.description}</p>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="bg-white p-4 rounded-md shadow-sm">
                          <h4 className="font-medium text-rose-500 mb-2">
                            Common Feelings
                          </h4>
                          <ul className="space-y-1">
                            {step.feelings.map((feeling, i) => (
                              <li key={i} className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-rose-400"></span>
                                <span className="text-slate-700">
                                  {feeling}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>

                        <div className="bg-white p-4 rounded-md shadow-sm">
                          <h4 className="font-medium text-blue-500 mb-2">
                            Focus on Self-Care
                          </h4>
                          <ul className="space-y-1">
                            {step.selfCare.map((care, i) => (
                              <li key={i} className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-blue-400"></span>
                                <span className="text-slate-700">{care}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Ad Unit 2: Before Note on Timelines */}
            <div className="my-8">
              <ins
                className="adsbygoogle"
                style={{ display: "block" }}
                data-ad-client="ca-pub-2067522743161930"
                data-ad-slot="YOUR_AD_SLOT_ID_2" // Replace with actual ad slot ID
                data-ad-format="auto"
                data-full-width-responsive="true"></ins>
              <script>
                (adsbygoogle = window.adsbygoogle || []).push({});
              </script>
            </div>

            <div className="mt-12 bg-slate-100 p-6 rounded-lg border-l-4 border-amber-500">
              <h3 className="text-xl font-semibold mb-3 text-amber-600">
                A Note on Timelines
              </h3>
              <p className="text-slate-700">
                These timeframes are approximations and may vary significantly
                based on relationship length, circumstances of the breakup, your
                support system, and previous experiences. Some people move
                through phases more quickly, while others may take longer or
                experience phases in a different order. Trust your individual
                process.
              </p>
            </div>
          </div>
        )}

        {/* Resources Content */}
        {activeTab === "resources" && (
          <div>
            <p className="text-lg mb-8 text-slate-600">
              These carefully selected resources can provide additional support
              and guidance as you navigate your healing journey.
            </p>

            <div className="space-y-12">
              {resources.map((resource, index) => (
                <div key={index}>
                  <h3 className="text-2xl font-semibold mb-6 inline-block border-b-2 border-purple-500 pb-1">
                    {resource.category}
                  </h3>

                  <div className="grid md:grid-cols-3 gap-6">
                    {resource.items.map((item, i) => (
                      <div
                        key={i}
                        className="bg-white p-5 rounded-lg shadow-md hover:shadow-lg transition-shadow">
                        <h4 className="font-semibold text-lg mb-2 text-slate-800">
                          {item.title}
                        </h4>
                        {item.author && (
                          <p className="text-purple-600 text-sm mb-3">
                            by {item.author}
                          </p>
                        )}
                        {item.host && (
                          <p className="text-purple-600 text-sm mb-3">
                            hosted by {item.host}
                          </p>
                        )}
                        <p className="text-slate-600 text-sm">
                          {item.focus || item.features}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Ad Unit 2: Before Professional Support Note */}
            <div className="my-8">
              <ins
                className="adsbygoogle"
                style={{ display: "block" }}
                data-ad-client="ca-pub-2067522743161930"
                data-ad-slot="YOUR_AD_SLOT_ID_2" // Replace with actual ad slot ID
                data-ad-format="auto"
                data-full-width-responsive="true"></ins>
              <script>
                (adsbygoogle = window.adsbygoogle || []).push({});
              </script>
            </div>

            <div className="mt-12 bg-gradient-to-r from-slate-100 to-slate-200 p-6 rounded-lg">
              <h3 className="text-xl font-semibold mb-3">
                Professional Support
              </h3>
              <p className="text-slate-700 mb-4">
                While self-help resources are valuable, consider seeking
                professional support if you're experiencing:
              </p>
              <ul className="space-y-2 mb-4">
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 rounded-full bg-red-500 mt-2"></span>
                  <span>Persistent feelings of hopelessness or depression</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 rounded-full bg-red-500 mt-2"></span>
                  <span>Difficulty functioning in daily life</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 rounded-full bg-red-500 mt-2"></span>
                  <span>Thoughts of harming yourself</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 rounded-full bg-red-500 mt-2"></span>
                  <span>Unhealthy coping mechanisms like substance use</span>
                </li>
              </ul>
              <p className="text-slate-700">
                A therapist, counselor, or support group can provide
                personalized guidance and tools for your specific situation.
              </p>
            </div>
          </div>
        )}

        {/* Ad Unit 3: Before Call to Action */}
        <div className="my-8">
          <ins
            className="adsbygoogle"
            style={{ display: "block" }}
            data-ad-client="ca-pub-2067522743161930"
            data-ad-slot="YOUR_AD_SLOT_ID_3" // Replace with actual ad slot ID
            data-ad-format="auto"
            data-full-width-responsive="true"></ins>
          <script>(adsbygoogle = window.adsbygoogle || []).push({});</script>
        </div>

        {/* Call to Action */}
        <div className="mt-16 text-center">
          <h3 className="text-2xl font-bold mb-4">You Are Not Alone</h3>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-6">
            Remember that countless others have walked this path before you and
            found healing. Be patient and gentle with yourself as you navigate
            this journey.
          </p>
          <div className="inline-block bg-gradient-to-r from-purple-600 to-rose-500 p-px rounded-lg">
            <button className="bg-white hover:bg-slate-50 px-8 py-3 rounded-lg font-medium transition-colors">
              Join Our Support Community
            </button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default SelfCareAfterBreakup;
